import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchStartups } from '../api';
import StartupCard from '../components/StartupCard';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const iconCache = {};
function riskIcon(level) {
  const key = level || 'none';
  if (iconCache[key]) return iconCache[key];
  const color = level === 'vert' ? '#27AE60' : level === 'orange' ? '#F39C12' : level === 'rouge' ? '#E74C3C' : '#2E75B6';
  iconCache[key] = L.divIcon({
    className: '',
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
    iconSize: [12, 12], iconAnchor: [6, 6],
  });
  return iconCache[key];
}

function MapBoundsUpdater({ data }) {
  const map = useMap();
  useEffect(() => {
    try {
      const pts = data.filter(p => p.latitude && p.longitude);
      if (pts.length > 0 && map && map.getContainer()) {
        const bounds = L.latLngBounds(pts.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]));
        setTimeout(() => {
          try { map.invalidateSize(); map.fitBounds(bounds, { padding: [30, 30] }); } catch(e) {}
        }, 100);
      }
    } catch(e) {}
  }, [data, map]);
  return null;
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState({ data: [], total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid | map
  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    secteur: searchParams.get('secteur') || '',
    min_note: searchParams.get('min_note') || '',
    adressage_client: searchParams.get('adressage_client') || '',
    ca: searchParams.get('ca') || '',
    round_levee: searchParams.get('round_levee') || '',
    stade_dev: '', region: '', trophy: '', risk_level: '',
    sort: 'note_sectorielle', order: 'DESC',
    page: 1, limit: viewMode === 'map' ? 500 : 20,
  });

  const doSearch = useCallback(async () => {
    setLoading(true);
    const params = { ...filters, limit: viewMode === 'map' ? 500 : 20 };
    const clean = {};
    Object.entries(params).forEach(([k, v]) => { if (v) clean[k] = v; });
    try { setResults(await searchStartups(clean)); }
    catch (err) { console.error(err); }
    setLoading(false);
  }, [filters, viewMode]);

  useEffect(() => { doSearch(); }, [doSearch]);
  const updateFilter = (key, value) => setFilters(f => ({ ...f, [key]: value, page: 1 }));

  return (
    <div style={{ padding: '32px 0' }}>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css" />
      <div className="container">
        <div className="search-bar" style={{ marginBottom: 24 }}>
          <input type="text" placeholder="Rechercher une startup par nom, secteur, sous-domaine..."
            value={filters.q} onChange={(e) => updateFilter('q', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
          <button className="btn btn-accent" onClick={doSearch}>Rechercher</button>
        </div>

        <div className="layout-search">
          <div className="filters-panel">
            <h3 style={{ marginBottom: 20, fontSize: 16 }}>Filtres</h3>

            <div className="filter-group">
              <label>Secteur</label>
              <select value={filters.secteur} onChange={(e) => updateFilter('secteur', e.target.value)}>
                <option value="">Tous</option>
                {['Fintech','Insurtech','Regtech','Biotech','Greentech','Medtech','SaaS','HealthTech','Immotech'].map(s =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="filter-group">
              <label>Note ISI minimum</label>
              <select value={filters.min_note} onChange={(e) => updateFilter('min_note', e.target.value)}>
                <option value="">Toutes</option>
                <option value="8">8+ (Excellent)</option>
                <option value="7">7+ (Résilient)</option>
                <option value="5">5+</option>
                <option value="4">4+ (Vigilance)</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Stade de développement</label>
              <select value={filters.stade_dev} onChange={(e) => updateFilter('stade_dev', e.target.value)}>
                <option value="">Tous</option>
                {['Pré-amorçage','Amorçage','Série A','Série B','Série C+'].map(s =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="filter-group">
              <label>Clientèle</label>
              <select value={filters.adressage_client} onChange={(e) => updateFilter('adressage_client', e.target.value)}>
                <option value="">Tous</option>
                <option value="B2B">B2B</option>
                <option value="B2C">B2C</option>
                <option value="B2B2C">B2B2C</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Indicateur de risque</label>
              <select value={filters.risk_level} onChange={(e) => updateFilter('risk_level', e.target.value)}>
                <option value="">Tous</option>
                <option value="vert">Vert (résilient)</option>
                <option value="orange">Orange (vigilance)</option>
                <option value="rouge">Rouge (critique)</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Trophées</label>
              <select value={filters.trophy} onChange={(e) => updateFilter('trophy', e.target.value)}>
                <option value="">Toutes</option>
                <option value="true">Participantes uniquement</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Trier par</label>
              <select value={filters.sort} onChange={(e) => updateFilter('sort', e.target.value)}>
                <option value="note_sectorielle">Note ISI</option>
                <option value="note_pivot">Note Pivot</option>
                <option value="name">Nom</option>
                <option value="created_at">Date</option>
              </select>
            </div>

            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={doSearch}>
              Appliquer les filtres
            </button>
          </div>

          <div>
            {/* View mode toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ color: 'var(--text-light)', fontSize: 14 }}>
                {results.total} startup{results.total !== 1 ? 's' : ''} trouvée{results.total !== 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-secondary)', borderRadius: 8, padding: 3 }}>
                {[
                  { key: 'grid', label: 'Grille' },
                  { key: 'map', label: 'Carte' },
                ].map(m => (
                  <button key={m.key} onClick={() => setViewMode(m.key)}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                      background: viewMode === m.key ? 'white' : 'transparent',
                      color: viewMode === m.key ? 'var(--primary)' : 'var(--text-light)',
                      boxShadow: viewMode === m.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="loading">Chargement...</div>
            ) : viewMode === 'grid' ? (
              <>
                <div className="grid-cards">
                  {results.data.map(s => <StartupCard key={s.id} startup={s} />)}
                </div>
                {results.totalPages > 1 && (
                  <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 24, flexWrap: 'wrap' }}>
                    <button disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                      style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'white', cursor: filters.page <= 1 ? 'not-allowed' : 'pointer', opacity: filters.page <= 1 ? 0.4 : 1, fontSize: 16, fontFamily: 'inherit', lineHeight: 1 }}>
                      &#8592;
                    </button>
                    {(() => {
                      const tp = results.totalPages;
                      const cp = filters.page;
                      const pages = [];
                      // Always show first 2 pages
                      for (let i = 1; i <= Math.min(2, tp); i++) pages.push(i);
                      // Pages around current
                      for (let i = Math.max(3, cp - 1); i <= Math.min(tp - 2, cp + 1); i++) pages.push(i);
                      // Always show last 2 pages
                      for (let i = Math.max(tp - 1, 3); i <= tp; i++) pages.push(i);
                      // Deduplicate and sort
                      const unique = [...new Set(pages)].sort((a, b) => a - b);
                      const rendered = [];
                      for (let i = 0; i < unique.length; i++) {
                        if (i > 0 && unique[i] - unique[i - 1] > 1) {
                          rendered.push(<span key={`ellipsis-${i}`} style={{ padding: '4px 6px', color: 'var(--text-light)', fontSize: 13, userSelect: 'none' }}>...</span>);
                        }
                        const p = unique[i];
                        rendered.push(
                          <button key={p} onClick={() => setFilters(f => ({ ...f, page: p }))}
                            style={{ minWidth: 36, padding: '8px 10px', border: p === cp ? '2px solid var(--primary)' : '1px solid var(--border)',
                              borderRadius: 8, background: p === cp ? 'var(--primary)' : 'white',
                              color: p === cp ? 'white' : 'var(--text)', cursor: 'pointer', fontSize: 13,
                              fontWeight: p === cp ? 700 : 400, fontFamily: 'inherit' }}>
                            {p}
                          </button>
                        );
                      }
                      return rendered;
                    })()}
                    <button disabled={filters.page >= results.totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                      style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'white', cursor: filters.page >= results.totalPages ? 'not-allowed' : 'pointer', opacity: filters.page >= results.totalPages ? 0.4 : 1, fontSize: 16, fontFamily: 'inherit', lineHeight: 1 }}>
                      &#8594;
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* MAP VIEW */
              (() => {
                const withAddress = results.data.filter(s => s.adresse && s.latitude && s.longitude);
                return (
              <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    Carte de l'annuaire
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                    <strong style={{ color: 'var(--primary)' }}>{withAddress.length}</strong> startups affichées sur la carte
                    {' '} / {results.total} dans l'annuaire
                    {withAddress.length < results.total && (
                      <span style={{ fontSize: 11 }}> (seules les startups avec adresse postale sont affichées)</span>
                    )}
                  </div>
                </div>
                <div style={{ height: 600 }}>
                  <MapContainer key="search-map" center={[46.8, 2.5]} zoom={6} style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true} zoomControl={true}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapBoundsUpdater data={withAddress} />
                    {withAddress.map(s => (
                        <Marker key={s.id} position={[parseFloat(s.latitude), parseFloat(s.longitude)]}
                          icon={riskIcon(s.risk_level)}>
                          <Popup>
                            <div style={{ minWidth: 200 }}>
                              <strong style={{ fontSize: 14 }}>{s.name}</strong>
                              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{s.categorie} — {s.sous_domaine}</div>
                              {s.note_sectorielle ? (
                                <div style={{ marginTop: 6, fontWeight: 700, fontSize: 16,
                                  color: parseFloat(s.note_sectorielle) > 7 ? '#27AE60' : parseFloat(s.note_sectorielle) >= 4 ? '#F39C12' : '#E74C3C' }}>
                                  {parseFloat(s.note_sectorielle).toFixed(2)}/10
                                </div>
                              ) : (
                                <div style={{ marginTop: 6, fontSize: 12, color: '#BDBDBD', fontStyle: 'italic' }}>Pas de score</div>
                              )}
                              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{s.adresse}</div>
                              <a href={`/startup/${s.id}`} style={{ display: 'inline-block', marginTop: 8, padding: '4px 12px',
                                background: 'var(--primary)', color: 'white', borderRadius: 6, fontSize: 11, textDecoration: 'none' }}>
                                Voir la fiche
                              </a>
                            </div>
                          </Popup>
                        </Marker>
                    ))}
                  </MapContainer>
                </div>
                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-light)' }}>
                  <span>{withAddress.length} startups localisées sur {results.total}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#27AE60' }} /> Vert
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F39C12' }} /> Orange
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#E74C3C' }} /> Rouge
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#2E75B6' }} /> Sans score
                  </span>
                </div>
              </div>
                );
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
